// Shared GPU plumbing for the VFX pools: dirty-span uploads and the one additive material shape they all use.

import { AdditiveBlending, DoubleSide, ShaderMaterial, type BufferAttribute, type IUniform } from 'three'

/** One pending GPU upload range of an attribute, in array elements (BufferAttribute.updateRanges entry). */
interface UpdateRange {
  start: number
  count: number
}

/**
 * Tracks the span of pool items written since the last flush, so a frame uploads one contiguous range per attribute
 * instead of the whole pool. Each attribute keeps one preallocated range object: a flush allocates nothing, and a
 * flush before the renderer uploaded the previous one (the renderer clears updateRanges after it uploads) widens the
 * pending range instead of dropping it. The span must be the only writer of its attributes' update ranges.
 */
export class DirtySpan {
  private first = Number.POSITIVE_INFINITY
  private end = 0
  private readonly ranges: readonly UpdateRange[]

  constructor(private readonly attributes: readonly BufferAttribute[]) {
    this.ranges = attributes.map(() => ({ start: 0, count: 0 }))
  }

  mark(item: number, count: number): void {
    if (item < this.first) this.first = item
    if (item + count > this.end) this.end = item + count
  }

  get empty(): boolean {
    return this.end <= this.first
  }

  /** Flags the dirty items of each attribute for upload, then clears the span. */
  flush(): void {
    if (this.empty) return
    for (let i = 0; i < this.attributes.length; i++) {
      const attribute = this.attributes[i] as BufferAttribute
      const range = this.ranges[i] as UpdateRange
      const start = this.first * attribute.itemSize
      const end = this.end * attribute.itemSize
      if (attribute.updateRanges.length === 0) {
        range.start = start
        range.count = end - start
        attribute.updateRanges.push(range)
      } else {
        const pendingEnd = Math.max(range.start + range.count, end)
        range.start = Math.min(range.start, start)
        range.count = pendingEnd - range.start
      }
      attribute.needsUpdate = true
    }
    this.first = Number.POSITIVE_INFINITY
    this.end = 0
  }
}

/** Additive, depth-tested, depth-read-only: the emissive-layer look (docs/ART-DIRECTION.md §2.5, §5). */
export function additiveMaterial(vertexShader: string, fragmentShader: string, uniforms: Record<string, IUniform>): ShaderMaterial {
  return new ShaderMaterial({
    uniforms,
    vertexShader,
    fragmentShader,
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
    side: DoubleSide,
  })
}
